/**
 * Plot page builder (docs/opencadstudio-parity/02-plot-pdf-ctb.md task 2.4).
 * Resolves the ledger into PlotContent — the same chain EntityLayer draws
 * with (BYLAYER via entityLineStyle, DRAWORDER, FILLMODE, onSheet filters),
 * plus the paper-space viewport projection of PaperSpaceCadLayer:
 *
 *   paper = vp.center + (model - viewCenter) · vp.scale
 *
 * Layout plots emit paper-space items (viewports clip their model content
 * to the frame / VPCLIP polygon); model plots emit model-space items for
 * extents/display/window. Draw order follows the OCS kind ranking:
 * wire-fill 0, hatch 1, wire 2, text 3, each by renderOrder/sequence.
 */

import { useAppStore } from '../../state/store';
import { useLayoutStore, type CadLayout, type CadViewport } from '../../state/useLayoutStore';
import { useBlockStore } from '../../state/commandAuxStores';
import { useTextStyleStore, decodeMText } from '../../tools/cad/TextTools';
import { entityToPolyline } from '../../core/commands/runners/lib';
import { entityLineStyle } from '../../rendering/entityLineStyle';
import { bakeDimensionBlocks } from '../cad/dxf/dimBlockBake';
import { colorToAci } from './aciColors';
import type { Entity, Layer } from '../../state/store';
import type {
    PlotContent, PlotFill, PlotHatch, PlotItem, PlotOptions, PlotText, PlotWire, PlotWindow,
} from './types';

/** Viewport chrome / 3D-solid types never plotted (EntityLayer cad2D rule). */
const SOLID_ENTITY_TYPES = new Set([
    'wall', 'door', 'window', 'floor', 'column', 'roof', 'ceiling',
    'stair', 'railing', 'ramp', 'component', 'area', 'toposolid',
    'loft', 'extrude', 'nurbs-surface', 'nurbs-polysurf', 'subd', 'mesh',
]);

/** OCS cull margin: 10 mm of paper. */
const CULL_MARGIN_M = 0.01;

/** Paper ink: the sheet is white, so unset/white inks plot black. */
function paperInk(c: string | undefined): string {
    return !c || c === '#ffffff' ? '#000000' : c;
}

export interface BuildPlotContentParams {
    plotType: 'layout' | 'extents' | 'window' | 'display';
    /** Layout id for layout plots; null = model-space plot. */
    layoutId: string | null;
    /** Model window for plotType 'window'. */
    window?: PlotWindow;
    options: PlotOptions;
}

interface Ctx {
    items: PlotItem[];
    sequence: number;
    options: PlotOptions;
    /** Model-space window used for infinite-line clipping (null = none). */
    modelWindow: { minX: number; minY: number; maxX: number; maxY: number } | null;
}

/** Build the plot content of one page. */
export function buildPlotContent(params: BuildPlotContentParams): PlotContent {
    const { entities, layers } = useAppStore.getState();
    const options = params.options;
    const ctx: Ctx = { items: [], sequence: 0, options, modelWindow: null };

    if (params.plotType === 'layout') {
        const layouts = useLayoutStore.getState().layouts;
        const layout = layouts.find((l) => l.id === params.layoutId);
        if (!layout) return { items: [], bounds: null };
        buildLayoutPage(ctx, layout, entities, layers);
    } else {
        if (params.plotType === 'window' && params.window) {
            const w = params.window;
            ctx.modelWindow = {
                minX: Math.min(w.x1, w.x2), minY: Math.min(w.y1, w.y2),
                maxX: Math.max(w.x1, w.x2), maxY: Math.max(w.y1, w.y2),
            };
        }
        buildModelPage(ctx, entities, layers);
    }

    const bounds = computeBounds(ctx.items);
    // Global draw order: kind ranking first, then render sequence.
    const kindRank: Record<PlotItem['kind'], number> = { fill: 0, hatch: 1, wire: 2, text: 3 };
    const items = ctx.items.slice().sort((a, b) =>
        (kindRank[a.kind] - kindRank[b.kind]) || (a.sequence - b.sequence));
    return { items, bounds };
}

function computeBounds(items: PlotItem[]): PlotContent['bounds'] {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const absorb = (x: number, y: number) => {
        if (x < minX) minX = x; if (y < minY) minY = y;
        if (x > maxX) maxX = x; if (y > maxY) maxY = y;
    };
    items.forEach((item) => {
        if (item.kind === 'text') { absorb(item.x, item.y); return; }
        if (item.kind === 'wire') { item.points.forEach(([x, y]) => absorb(x, y)); return; }
        item.ring.forEach(([x, y]) => absorb(x, y));
        if (item.kind === 'hatch') item.segments.forEach((seg) => seg.forEach(([x, y]) => absorb(x, y)));
    });
    return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
}

// ---------------------------------------------------------------------------
// Model-space page (extents / display / window)
// ---------------------------------------------------------------------------

function buildModelPage(
    ctx: Ctx,
    entities: Record<string, Entity>,
    layers: Record<string, Layer>,
): void {
    Object.values(entities).forEach((e) => {
        if (!e.visible || e.data?.onSheet) return;
        const layer = layers[e.layer];
        if (layer && (!layer.visible || layer.frozen)) return;
        // Plottable flag (Faz 5.3): layers with Plot off stay visible in the
        // view but never emit plot geometry.
        if (layer && layer.plottable === false) return;
        if (SOLID_ENTITY_TYPES.has(e.type)) return;
        emitEntity(ctx, e, layer, null, null, 1, 0);
    });
}

// ---------------------------------------------------------------------------
// Layout page (paper space)
// ---------------------------------------------------------------------------

function buildLayoutPage(
    ctx: Ctx,
    layout: CadLayout,
    entities: Record<string, Entity>,
    layers: Record<string, Layer>,
): void {
    // Viewport projection: paper = vp.center + (model - viewCenter) · scale.
    layout.viewports.forEach((vp) => {
        const s = vp.scale > 0 ? vp.scale : 1;
        // Model-space rect visible through this viewport (for infinite lines).
        const modelRect = {
            minX: vp.viewCx - (vp.w / 2) / s, maxX: vp.viewCx + (vp.w / 2) / s,
            minY: vp.viewCy - (vp.h / 2) / s, maxY: vp.viewCy + (vp.h / 2) / s,
        };
        const clip: [number, number][] = (vp.clip && vp.clip.length >= 3)
            ? vp.clip.map(([x, y]) => [x, y])
            : [
                [vp.cx - vp.w / 2, vp.cy - vp.h / 2],
                [vp.cx + vp.w / 2, vp.cy - vp.h / 2],
                [vp.cx + vp.w / 2, vp.cy + vp.h / 2],
                [vp.cx - vp.w / 2, vp.cy + vp.h / 2],
            ];
        const frozen = new Set(vp.frozenLayers);
        const project = (x: number, y: number): [number, number] =>
            [vp.cx + (x - vp.viewCx) * s, vp.cy + (y - vp.viewCy) * s];

        Object.values(entities).forEach((e) => {
            if (!e.visible || e.data?.onSheet) return;
            const layer = layers[e.layer];
            if (layer && (!layer.visible || layer.frozen)) return;
            // Plottable flag (Faz 5.3) + VPLAYER set (names; raw ids too).
            if (layer && layer.plottable === false) return;
            if (frozen.has(e.layer) || frozen.has(layer?.name ?? '')) return;
            if (SOLID_ENTITY_TYPES.has(e.type)) return;
            emitEntity(ctx, e, layer, clip, project, s, 0, modelRect);
        });

        // Viewport title below the frame (2D behavior parity).
        if (vp.showTitle) {
            ctx.items.push({
                kind: 'text',
                x: vp.cx, y: vp.cy - vp.h / 2 - 0.02,
                rotation: 0,
                height: 0.018,
                content: `${vp.title || 'View'} (${vp.scale}:1)`,
                color: '#111111',
                opacity: 1,
                sequence: ctx.sequence++,
                clip: null,
                aci: colorToAci('#111111'),
            });
        }
    });

    // Paper-space annotations owned by this layout, at paper coordinates.
    // Paper texts render at the fixed 0.02 sheet height in
    // PaperSpaceCadLayer — plot matches that on-screen look.
    Object.values(entities).forEach((e) => {
        if (!e.visible || e.data?.onSheet !== layout.id) return;
        const layer = layers[e.layer];
        if (layer && (!layer.visible || layer.frozen)) return;
        emitEntity(ctx, e, layer, null, null, 1, 0, undefined, undefined, 0.02);
    });
}

// ---------------------------------------------------------------------------
// Entity emission (shared by model + paper passes)
// ---------------------------------------------------------------------------

type ProjectFn = (x: number, y: number) => [number, number];

/**
 * Emits one entity into the current source space. `project` is null for
 * plain passes and set for viewport passes; `viewScale` scales dashes and
 * geometry-relative heights with the transform; `clip` is the viewport clip
 * polygon (already in the target space); `colorOverride` re-inks baked
 * dimension geometry; `textHeightOverride` pins annotation heights (paper
 * texts draw at the fixed sheet height).
 */
function emitEntity(
    ctx: Ctx,
    e: Entity,
    layer: Layer | undefined,
    clip: [number, number][] | null,
    project: ProjectFn | null,
    viewScale: number,
    depth: number,
    modelRect?: { minX: number; minY: number; maxX: number; maxY: number } | null,
    colorOverride?: string,
    textHeightOverride?: number,
): void {
    if (depth > 8) return;
    const d = e.data as Record<string, any>;
    // Dimension entities plot through their baked block geometry — the same
    // math the on-screen renderers use (dimBlockBake, Faz 1 parity).
    if (e.type === 'dimension') {
        const baked = bakeDimensionBlocks([e]).get(e.id);
        if (baked) {
            const dimColor = colorOverride
                ?? (typeof d.color === 'string' ? d.color : undefined);
            baked.entities.forEach((child) => {
                emitEntity(ctx, child, layer, clip, project, viewScale, depth + 1, modelRect, dimColor);
            });
            return;
        }
    }
    if (e.type === 'insert') {
        emitInsert(ctx, e, clip, project, viewScale, depth, modelRect, colorOverride);
        return;
    }

    // --- Effective style (BYLAYER chain, same as EntityLayer) ---
    const explicitColor = colorOverride ?? (typeof d.color === 'string' ? d.color : undefined);
    const color = paperInk(explicitColor ?? layer?.color);
    const style = entityLineStyle(e, layer);
    const aci = colorToAci(color);
    const opacity = ctx.options.plotTransparency ? style.opacity : 1;
    const seq = ctx.sequence++;
    // Physical pen width: entity pixel override → ~0.25 mm/px; layer value
    // is classic ACAD hundredths of a mm; default pen 0.25.
    const lwMm = typeof d.lineWeight === 'number' && d.lineWeight >= 1
        ? d.lineWeight * 0.25
        : typeof layer?.lineweight === 'number'
            ? layer.lineweight / 100
            : 0.25;
    const dash: [number, number] | null = style.dash
        ? [style.dash[0] * (viewScale || 1), style.dash[1] * (viewScale || 1)]
        : null;

    const T = (p: number[]): [number, number] => {
        if (project) return project(p[0] ?? 0, p[1] ?? 0);
        return [p[0] ?? 0, p[1] ?? 0];
    };

    switch (e.type) {
        case 'line': {
            if (!Array.isArray(d.start) || !Array.isArray(d.end)) return;
            // XLINE/RAY: clip the infinite carrier to the visible window.
            if (d.xline || d.ray) {
                const rect = modelRect ?? ctx.modelWindow;
                if (!rect) return;
                const segs = clipInfiniteLine(d.start, d.end, rect, !!d.ray);
                segs.forEach(([a, b]) => pushWire(ctx, [T(a), T(b)], color, lwMm, null, opacity, seq, clip, aci));
                return;
            }
            pushWire(ctx, [T(d.start), T(d.end)], color, lwMm, dash, opacity, seq, clip, aci);
            return;
        }
        case 'polyline':
        case 'polygon':
        case 'rectangle':
        case 'circle':
        case 'arc':
        case 'ellipse':
        case 'curve':
        case 'nurbs-curve': {
            const pts = entityToPolyline(e);
            if (!pts || pts.length < 2) return;
            pushWire(ctx, pts.map((p) => T(p)), color, lwMm, dash, opacity, seq, clip, aci);
            return;
        }
        case 'hatch': {
            const ring = (d.boundary as number[][]) ?? [];
            if (ring.length < 3) return;
            const segments = (d.segments as number[][]) ?? [];
            const ringM = ring.map((p) => T(p));
            const segM = segments
                .filter((s) => s.length >= 6)
                .map((s) => [T([s[0], s[1], s[2]]), T([s[3], s[4], s[5]])] as [[number, number], [number, number]]);
            ctx.items.push({
                kind: 'hatch', ring: ringM, holes: [], segments: segM,
                solid: !!d.fill,
                color, opacity, sequence: seq, clip, aci,
            } satisfies PlotHatch);
            return;
        }
        case 'solid': {
            // Flat 2D plates (SOLID / wipeout / gradient carriers).
            if (!d.flat && !d.wipeout) return;
            const ring = entityToPolyline(e);
            if (!ring || ring.length < 3) return;
            ctx.items.push({
                kind: 'fill',
                ring: ring.map((p) => T(p)),
                holes: [],
                color: d.wipeout ? '#ffffff' : color,
                role: d.wipeout ? 'wipeout' : 'fill',
                opacity,
                sequence: seq,
                clip,
                aci: d.wipeout ? 7 : aci,
            } satisfies PlotFill);
            return;
        }
        case 'text':
        case 'mtext': {
            const pos = Array.isArray(d.position) ? d.position : [0, 0, 0];
            const [x, y] = T(pos);
            const styles = useTextStyleStore.getState().styles;
            const style = styles[d.styleId] ?? styles['standard'];
            const height = typeof d.height === 'number' && d.height > 0
                ? d.height
                : textHeightOverride ?? (style?.fontSize ?? 0.3) * (viewScale || 1);
            const decoded = decodeMText(String(d.content ?? '')).text;
            if (!decoded.trim()) return;
            const rot = typeof d.rotation === 'number' ? d.rotation : 0;
            ctx.items.push({
                kind: 'text', x, y, rotation: rot, height,
                content: decoded, color,
                opacity, sequence: seq, clip, aci,
            } satisfies PlotText);
            return;
        }
        case 'leader': {
            if (!Array.isArray(d.tip) || !Array.isArray(d.landing)) return;
            pushWire(ctx, [T(d.tip), T(d.landing)], color, lwMm, null, opacity, seq, clip, aci);
            const [lx, ly] = T(d.landing);
            ctx.items.push({
                kind: 'text', x: lx + 0.15 * (viewScale || 1), y: ly,
                rotation: 0, height: textHeightOverride ?? 0.25 * (viewScale || 1),
                content: String(d.text ?? ''), color,
                opacity, sequence: seq, clip, aci,
            } satisfies PlotText);
            return;
        }
        case 'table': {
            const cells = (d.cells as string[][]) ?? [];
            const pos = (d.position as number[]) ?? [0, 0, 0];
            const cw = d.colWidth || 2, rh = d.rowHeight || 0.6;
            if (cells.length === 0) return;
            const cols = cells.reduce((m: number, r: string[]) => Math.max(m, r.length), 0);
            const w = cols * cw, h = cells.length * rh;
            const grid: number[][][] = [];
            for (let r = 0; r <= cells.length; r++) grid.push([[pos[0], pos[1] - r * rh], [pos[0] + w, pos[1] - r * rh]]);
            for (let c = 0; c <= cols; c++) grid.push([[pos[0] + c * cw, pos[1]], [pos[0] + c * cw, pos[1] - h]]);
            grid.forEach((g) => pushWire(ctx, g.map((p) => T(p)), color, 0.25, null, opacity, ctx.sequence++, clip, aci));
            cells.forEach((row: string[], r: number) => row.forEach((cell: string, c: number) => {
                const [tx, ty] = T([pos[0] + c * cw + 0.1, pos[1] - r * rh - rh * 0.6]);
                ctx.items.push({
                    kind: 'text', x: tx, y: ty, rotation: 0,
                    height: textHeightOverride ?? rh * 0.5,
                    content: String(cell ?? ''), color, opacity,
                    sequence: ctx.sequence++, clip, aci,
                } satisfies PlotText);
            }));
            return;
        }
        case 'attdef': {
            const pos = (d.position as number[]) ?? [0, 0, 0];
            const [x, y] = T(pos);
            ctx.items.push({
                kind: 'text', x, y, rotation: 0,
                height: textHeightOverride ?? 0.22 * (viewScale || 1),
                content: `<${String(d.tag ?? 'TAG')}>`, color,
                opacity, sequence: seq, clip, aci,
            } satisfies PlotText);
            return;
        }
        case 'point': {
            const p = (d.position as number[]) ?? [0, 0, 0];
            const [x, y] = T(p);
            const r = 0.1 * (viewScale || 1);
            pushWire(ctx, [[x - r, y], [x + r, y]], color, lwMm, null, opacity, ctx.sequence++, clip, aci);
            pushWire(ctx, [[x, y - r], [x, y + r]], color, lwMm, null, opacity, seq, clip, aci);
            return;
        }
        default:
            return;
    }
}

function pushWire(
    ctx: Ctx,
    points: [number, number][],
    color: string,
    lineweight: number,
    dash: [number, number] | null,
    opacity: number,
    sequence: number,
    clip: [number, number][] | null,
    aci: number,
): void {
    if (points.length < 2) return;
    // Culling: drop strokes fully outside the clip window (OCS cull pass).
    if (clip) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        points.forEach(([x, y]) => {
            if (x < minX) minX = x; if (y < minY) minY = y;
            if (x > maxX) maxX = x; if (y > maxY) maxY = y;
        });
        let cMinX = Infinity, cMinY = Infinity, cMaxX = -Infinity, cMaxY = -Infinity;
        clip.forEach(([x, y]) => {
            if (x < cMinX) cMinX = x; if (y < cMinY) cMinY = y;
            if (x > cMaxX) cMaxX = x; if (y > cMaxY) cMaxY = y;
        });
        if (minX - cMaxX > CULL_MARGIN_M || cMinX - maxX > CULL_MARGIN_M
            || minY - cMaxY > CULL_MARGIN_M || cMinY - maxY > CULL_MARGIN_M) return;
    }
    ctx.items.push({
        kind: 'wire', points, color, lineweight, dash, opacity, sequence, clip, aci,
    } satisfies PlotWire);
}

// ---------------------------------------------------------------------------
// Block references (insert) — expand like BlockRefMesh renders
// ---------------------------------------------------------------------------

function emitInsert(
    ctx: Ctx,
    e: Entity,
    clip: [number, number][] | null,
    project: ProjectFn | null,
    viewScale: number,
    depth: number,
    modelRect: { minX: number; minY: number; maxX: number; maxY: number } | null | undefined,
    colorOverride?: string,
): void {
    const d = e.data as Record<string, any>;
    const def = useBlockStore.getState().defs[String(d.blockName ?? '')];
    if (!def) return;
    const pos = (Array.isArray(d.position) ? d.position : [0, 0, 0]) as number[];
    const scale = typeof d.scale === 'number' && d.scale > 0 ? d.scale : 1;
    const rot = typeof d.rotation === 'number' ? d.rotation : 0;
    const rad = (rot * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    // Same transform BlockRefMesh uses: local · scale, rotate around Z,
    // translate. Children inherit the geometry transform (dash scaling).
    const totalScale = scale * (viewScale || 1);
    const nested: ProjectFn = (x, y) => {
        const lx = x * scale, ly = y * scale;
        if (project) return project(pos[0] + lx * cos - ly * sin, pos[1] + lx * sin + ly * cos);
        return [pos[0] + lx * cos - ly * sin, pos[1] + lx * sin + ly * cos];
    };
    def.entities.forEach((child) => {
        emitEntity(ctx, child, undefined, clip, nested, totalScale, depth + 1, modelRect, colorOverride);
    });
}

// ---------------------------------------------------------------------------
// Infinite line clipping (xline/ray against the visible model rect)
// ---------------------------------------------------------------------------

/** Intersect an infinite/ray line with a rect; returns 0 or 1 segment. */
function clipInfiniteLine(
    startRaw: number[], endRaw: number[],
    rect: { minX: number; minY: number; maxX: number; maxY: number },
    ray: boolean,
): [number[], number[]][] {
    const x0 = startRaw[0] ?? 0, y0 = startRaw[1] ?? 0;
    let dx = (endRaw[0] ?? 0) - x0, dy = (endRaw[1] ?? 0) - y0;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return [];
    dx /= len; dy /= len;
    // Parametric slab intersection of the carrier line.
    let t0 = -Infinity, t1 = Infinity;
    const slab = (p: number, dir: number, lo: number, hi: number) => {
        if (Math.abs(dir) < 1e-12) return p >= lo && p <= hi;
        const ta = (lo - p) / dir, tb = (hi - p) / dir;
        t0 = Math.max(t0, Math.min(ta, tb));
        t1 = Math.min(t1, Math.max(ta, tb));
        return true;
    };
    if (!slab(x0, dx, rect.minX, rect.maxX)) return [];
    if (!slab(y0, dy, rect.minY, rect.maxY)) return [];
    if (ray) t0 = Math.max(t0, 0);
    if (t1 <= t0) return [];
    const span = Math.max(rect.maxX - rect.minX, rect.maxY - rect.minY);
    const tLo = Math.max(t0, -span * 10), tHi = Math.min(t1, span * 10);
    if (tHi <= tLo) return [];
    return [[
        [x0 + dx * tLo, y0 + dy * tLo, 0],
        [x0 + dx * tHi, y0 + dy * tHi, 0],
    ]];
}

/**
 * Model-space window of a whole drawing (extents + display approximation):
 * the union bounds of plottable model content, inflated 5% like the OCS
 * extents job.
 */
export function modelExtents(): { minX: number; minY: number; maxX: number; maxY: number } | null {
    const { entities, layers } = useAppStore.getState();
    const ctx: Ctx = { items: [], sequence: 0, options: { objectLineweights: false, scaleLineweights: false, plotTransparency: false, mergeLines: false, plotStamp: false, quality: 'medium' }, modelWindow: null };
    buildModelPage(ctx, entities, layers);
    const b = computeBounds(ctx.items);
    if (!b) return null;
    const padX = (b.maxX - b.minX) * 0.05, padY = (b.maxY - b.minY) * 0.05;
    return {
        minX: b.minX - padX, minY: b.minY - padY,
        maxX: b.maxX + padX, maxY: b.maxY + padY,
    };
}

/** Layout count/type helper for the dialogs (viewport sizes at a glance). */
export function layoutViewportSummary(layout: CadLayout): string {
    return layout.viewports.length === 0
        ? '—'
        : layout.viewports.map((vp: CadViewport) => `1:${Math.round(1 / (vp.scale > 0 ? vp.scale : 1))}`).join(', ');
}
